import type {
  ComponentType,
  CSSProperties,
  ReactNode,
  SVGProps,
} from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import CheckReadLinear from "~icons/solar/check-read-linear";
import { cn } from "../../lib/utils";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

// ---------------------------------------------------------------------------
// Context so items can close the menu
// ---------------------------------------------------------------------------

const DropdownContext = createContext<{ close: () => void } | null>(null);

/** Close the enclosing Dropdown — for custom panels with their own Apply button. */
// eslint-disable-next-line react-refresh/only-export-components
export function useDropdownClose(): () => void {
  const ctx = useContext(DropdownContext);
  return ctx?.close ?? (() => {});
}

// ---------------------------------------------------------------------------
// Dropdown
// ---------------------------------------------------------------------------

export interface DropdownTriggerProps {
  open: boolean;
  toggle: () => void;
  "aria-haspopup": "menu";
  "aria-expanded": boolean;
}

export interface DropdownProps {
  /** Render the trigger; spread `props` onto it and wire `toggle` to onClick. */
  trigger: (props: DropdownTriggerProps) => ReactNode;
  /** Which edge of the trigger the menu aligns to. */
  align?: "start" | "end";
  /** Minimum menu width. */
  minWidth?: number;
  children: ReactNode;
  className?: string;
}

const GAP = 6;

/**
 * Menu rendered in a portal so it is never clipped by an overflow-hidden
 * ancestor. Closes on outside click, Escape, and navigation.
 */
export function Dropdown({
  trigger,
  align = "start",
  minWidth = 180,
  children,
  className,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties>();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  // Close on navigation.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Position against the trigger; keep it on-screen.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      if (!a) return;
      const menuH = menuRef.current?.offsetHeight ?? 0;
      const below = a.bottom + GAP + menuH <= window.innerHeight;
      const top = below ? a.bottom + GAP : Math.max(8, a.top - GAP - menuH);
      setStyle(
        align === "end"
          ? { top, right: Math.max(8, window.innerWidth - a.right), minWidth }
          : { top, left: Math.max(8, a.left), minWidth }
      );
    };
    place();
    menuRef.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')
      ?.focus();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, align, minWidth]);

  // Outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <span ref={anchorRef} className="inline-flex">
        {trigger({
          open,
          toggle,
          "aria-haspopup": "menu",
          "aria-expanded": open,
        })}
      </span>
      {open &&
        createPortal(
          <DropdownContext.Provider value={{ close }}>
            <div
              ref={menuRef}
              role="menu"
              style={style}
              className={cn(
                "fixed z-50 flex flex-col gap-0.5 rounded-2xl border border-black/[0.06] bg-white p-1.5 shadow-pop motion-safe:animate-dropdown-slide-in",
                !style && "invisible",
                className
              )}
            >
              {children}
            </div>
          </DropdownContext.Provider>,
          // Inside a showModal() dialog the rest of the document is inert, so
          // the menu must live in the dialog's subtree to stay clickable.
          anchorRef.current?.closest("dialog") ?? document.body
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export interface DropdownItemProps {
  children: ReactNode;
  icon?: IconType;
  onSelect?: () => void;
  /** Shows a check mark on the right. */
  selected?: boolean;
  danger?: boolean;
  disabled?: boolean;
  /** Keep the menu open after selecting. */
  keepOpen?: boolean;
  className?: string;
}

export function DropdownItem({
  children,
  icon: Icon,
  onSelect,
  selected = false,
  danger = false,
  disabled = false,
  keepOpen = false,
  className,
}: DropdownItemProps) {
  const ctx = useContext(DropdownContext);
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      aria-checked={selected || undefined}
      onClick={() => {
        onSelect?.();
        if (!keepOpen) ctx?.close();
      }}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-50",
        danger
          ? "text-error hover:bg-error-light"
          : "text-ink hover:bg-black/[0.04]",
        selected && !danger && "text-primary",
        className
      )}
    >
      {Icon && <Icon aria-hidden="true" className="size-4 shrink-0" />}
      <span className="flex-1 truncate">{children}</span>
      {selected && (
        <CheckReadLinear aria-hidden="true" className="size-4 shrink-0 text-primary" />
      )}
    </button>
  );
}

export function DropdownSeparator() {
  return <div role="separator" className="my-1 h-px bg-black/[0.06]" />;
}

export function DropdownLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
      {children}
    </div>
  );
}
